import { useCallback, useRef, useEffect, RefCallback } from 'react'

import { extractIndex } from './helpers'


/**
 *  Синхронизация высот строк в составной таблице
 */

type SavingItem = {
  height: number,
  writed: number,
}

const defaultSavingItem = { height: 0, writed: 0 }

type Props = {
  numbersOfChunks?: number,
  unobserveKey?: any,
  updateKey?: any,
  refFuncExternal?: RefCallback<HTMLTableRowElement>
}

export function useSyncRowsHeightsV2({
  numbersOfChunks = 2,
  unobserveKey,
  updateKey,
  refFuncExternal,
}: Props = {}): [RefCallback<HTMLTableRowElement>, () => void] {
  const sizes = useRef<Array<SavingItem>>([])
  const firstRender = useRef(true)

  const handleResize = useCallback((entries) => {
    entries.forEach(({ contentRect, target }) => {
      const { height } = contentRect
      if (!height) return // еще не отрисовалось полностью

      const rowIndex = extractIndex(target.className)
      if (rowIndex === null) {
        console.error('Не удалось узнать индекс строки таблицы (устновите класс "index_N" у Tr элементов)')
        return
      }
      if (!sizes.current[rowIndex]) { // еще нет данных
        sizes.current[rowIndex] = { ...defaultSavingItem }
      }

      if (sizes.current[rowIndex].writed > numbersOfChunks - 1) return // не нужно менять повторно

      // берем все элементы строки и вычисляем максимальный размер
      const currentRowItems = document.querySelectorAll(`tr.index_${rowIndex}`)
      const heights = [] as Array<number>
      for (let i = 0, l = currentRowItems.length; i < l; i++) { // eslint-disable-line no-plusplus
        heights[i] = currentRowItems[i].getBoundingClientRect().height
      }

      sizes.current[rowIndex].height = Math.max(...heights)
      sizes.current[rowIndex].writed += 1

      // назначаем новый размер элементам, когда был обход всех в строке
      if (sizes.current[rowIndex].writed > numbersOfChunks - 1) {
        for (let i = 0, l = currentRowItems.length; i < l; i++) { // eslint-disable-line no-plusplus
          (currentRowItems[i] as HTMLElement).style.height = `${sizes.current[rowIndex].height}px`
        }

        sizes.current[rowIndex].writed = 0
        sizes.current[rowIndex].height = 0
      }
    })
  }, [numbersOfChunks])

  const ro = useRef(new ResizeObserver(handleResize))

  const refFunc: RefCallback<HTMLTableRowElement> = useCallback((node) => {
    if (node) {
      ro.current.observe(node)
    }
    if (refFuncExternal) {
      refFuncExternal(node)
    }
  }, [])

  const handleUnobserveAll = useCallback(() => {
    ro.current.disconnect()
  }, [])

  // снятие наблюдения (например при смене страницы)
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    handleUnobserveAll()
  }, [unobserveKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // принудительное обновление высоты
  useEffect(() => {
    for (let j = 0; j < 100; j++) { // eslint-disable-line no-plusplus
      const currentRowItems = document.querySelectorAll(`tr.index_${j}`)
      if (currentRowItems.length === 0) break
      for (let i = 0, l = currentRowItems.length; i < l; i++) { // eslint-disable-line no-plusplus
        (currentRowItems[i] as HTMLElement).style.height = ''
      }
    }
  }, [updateKey])

  return [refFunc, handleUnobserveAll]
}
